// La table de marque (D-175, D-176), logique pure et testée. Hors compétition FFBB : amicaux, tournois du club, playground,
// entraînement. Elle écrit dans le MÊME journal que les stats en direct (D-046, src/domain/boxscore.ts) ; ce qui n'est pas
// une action du match (le chrono, la pause, la flèche de possession, la question « Victoire de … ? ») vit dans un état
// gardé sur le téléphone de la table et recopié dans la base à chaque envoi.
//
// Règles vérifiées sur les Règles officielles de basketball FIBA 2026 (en vigueur depuis le 1er octobre 2026) :
//  - art. 8 : 4 quarts-temps de 10 min, 2 min entre les QT 1-2 et 3-4 et avant chaque prolongation, mi-temps de 15 min,
//    prolongations de 5 min autant qu'il en faut ;
//  - art. 12 : flèche de possession alternée, retournée après chaque remise en jeu de possession alternée ; elle sert
//    aussi à commencer chaque quart-temps (sauf le premier) et chaque prolongation ;
//  - art. 16 : 1 (lancer franc), 2, 3 points ;
//  - art. 18 : 2 temps-morts en 1re mi-temps, 3 en 2e dont 2 au plus quand le chrono affiche 2:00 ou moins au 4e QT,
//    1 par prolongation, non reportés ; 1 min chacun ;
//  - art. 29 : 24 s, remis à 14 s dans les cas prévus ;
//  - art. 36 à 38 et 41 (nouveau en 2026) : la faute antisportive est remplacée par la faute disruptive (DI, ne compte pas
//    pour l'exclusion) et la faute flagrante (FL) ; un joueur est exclu avec 2 techniques (catégorie 1), 2 flagrantes ou
//    une de chaque ; 5 fautes : il sort ;
//  - art. 42 (ex-41) : une équipe est en pénalité après 4 fautes d'équipe dans le quart-temps (toutes les fautes de
//    joueur, techniques comprises) ; celles des prolongations comptent avec le 4e quart-temps.
// 3x3 (règles FIBA 3x3, réflexion D-175) : 10 min ou 21 points (dans le temps seulement), prolongation après 1 min : la
// première à marquer 2 points ; 1 et 2 points ; 12 s ; 6 fautes d'équipe puis 2 lancers francs (7e à 9e), 2 lancers et le
// ballon dès la 10e ; 1 temps-mort de 30 s par équipe.
// Tout est un PRÉRÉGLAGE modifiable : aucune règle de catégorie FFBB écrite en dur.
import { pointsOf, score, type Action, type ActionKind } from './boxscore';

export type Format = '5x5' | '3x3';
export type Side = 'us' | 'them';
export type View = 'simple' | 'full';

export interface Rules {
  format: Format;
  /** Périodes du temps réglementaire (4 en 5x5, 1 en 3x3) et leur durée ; durée d'une prolongation (0 : sans chrono). */
  periods: number; periodMin: number; otMin: number;
  /** Pause entre deux périodes (et avant une prolongation), mi-temps (5x5). */
  breakMin: number; halfMin: number;
  /** 5x5 : en pénalité APRÈS ce nombre de fautes d'équipe dans le QT (4) ; 3x3 : bonus au-delà (6, donc dès la 7e). */
  teamFouls: number;
  /** Fautes pour sortir (5) ; null : pas de limite (3x3). */
  playerFouls: number | null;
  /** Temps-morts : 1re mi-temps, 2e mi-temps, chaque prolongation ; 3x3 : [par match, 0, 0]. */
  timeouts: [number, number, number];
  /** Au plus ce nombre dans les 2 dernières minutes du dernier quart-temps (2) ; null : pas de limite. */
  lateMax: number | null;
  timeoutSec: number;
  /** Chrono des tirs (en option, éteint par défaut) : 24 s et le retour à 14 s ; 12 s en 3x3. */
  shot: number; shotShort: number | null;
  /** 3x3 : le score qui gagne dans le temps (21), puis en prolongation (2 points). */
  target: number | null; otTarget: number | null;
  /** Un chrono de match ; false : pas de chrono (le QT se change à la main). */
  clock: boolean;
}

const FIBA5: Rules = { format: '5x5', periods: 4, periodMin: 10, otMin: 5, breakMin: 2, halfMin: 15, teamFouls: 4, playerFouls: 5,
  timeouts: [2, 3, 1], lateMax: 2, timeoutSec: 60, shot: 24, shotShort: 14, target: null, otTarget: null, clock: true };
const FIBA3: Rules = { format: '3x3', periods: 1, periodMin: 10, otMin: 0, breakMin: 1, halfMin: 0, teamFouls: 6, playerFouls: null,
  timeouts: [1, 0, 0], lateMax: null, timeoutSec: 30, shot: 12, shotShort: null, target: 21, otTarget: 2, clock: true };

export const PRESETS: { id: string; label: string; sub: string; rules: Rules }[] = [
  { id: 'fiba5', label: 'FIBA 5x5', sub: '4 × 10 min · prolongations de 5 min', rules: FIBA5 },
  { id: 'jeunes5', label: 'Jeunes 5x5', sub: 'durées à régler (ici 4 × 8 min)', rules: { ...FIBA5, periodMin: 8, otMin: 3 } },
  { id: 'fiba3', label: 'FIBA 3x3', sub: '10 min ou 21 points', rules: FIBA3 },
  { id: 'libre3', label: '3x3 sans chrono', sub: 'playground : le premier à 21 points', rules: { ...FIBA3, clock: false } },
];
export const presetOf = (id: string) => PRESETS.find(p => p.id === id) ?? PRESETS[0]!;

/**
 * Un chrono (la période, la pause, les 24 s) : départ (heure de la base) + durée + pauses + corrections. Les heures sont
 * celles de la base, estimées sur le téléphone par l'horloge synchronisée des jeux (ClockSync) : un téléphone qui perd le
 * réseau continue de compter juste, deux téléphones (« Passer la main », la TV, le suivi des parents) lisent le même
 * temps, et une horloge commune peut piloter plusieurs tables (lot 3 : les terrains d'un tournoi).
 *  - `dur` : la durée ; `start` : le premier départ (null : pas encore parti) ;
 *  - `paused` : le total des arrêts de jeu passés ; `pausedAt` : l'arrêt en cours (null : le chrono tourne) ;
 *  - `fix` : les corrections (ms ajoutées au temps restant).
 */
export interface Clock { dur: number; start: number | null; paused: number; pausedAt: number | null; fix: number }
/** Une horloge commune (lot 3) : la table est « pilotée », temps courant forcé, chrono en lecture seule. */
export interface Common { by: string; clock: Clock; period?: number }
export interface TableState {
  v: 1;
  preset: string; rules: Rules; view: View;
  period: number;
  /** « play » : la période (chrono arrêté ou en marche) ; « break » : la pause qui s'écoule ; « end » : temps réglementaire fini. */
  phase: 'play' | 'break' | 'end';
  clock: Clock; brk: Clock | null;
  shotOn: boolean; shot: Clock;
  arrow: Side | null;
  to: { side: Side; until: number } | null;
  /** 3x3 : la question « Victoire de … ? » en attente (elle bloque les nouveaux points). */
  win: Side | null;
  finished: boolean;
  /** Les numéros adverses ajoutés à la main (« + N° »). */
  oppNums: number[];
  /** Les couleurs des deux équipes (#rrggbb ; null : celles par défaut) ; les noms (table d'entraînement). */
  colors: { us: string | null; them: string | null };
  names: { us: string | null; them: string | null };
  /** Piloté par une horloge commune (« Chrono commun · géré par l'organisatrice ») : seuls les points et les fautes se saisissent. */
  common: { by: string } | null;
}

/** Un chrono plein, arrêté ; `startAt` : lancé à cette heure. */
export const clk = (dur: number, startAt: number | null = null): Clock => ({ dur, start: startAt, paused: 0, pausedAt: null, fix: 0 });
export const periodMs = (r: Rules, p: number) => (p <= r.periods ? r.periodMin : r.otMin) * 60_000;
export const isOT = (r: Rules, p: number) => p > r.periods;

export function newState(presetId: string, now: number, o: { view?: View; rules?: Rules } = {}): TableState {
  const preset = presetOf(presetId);
  const rules = o.rules ?? { ...preset.rules, timeouts: [...preset.rules.timeouts] as Rules['timeouts'] };
  void now;
  return { v: 1, preset: preset.id, rules, view: o.view ?? 'simple', period: 1, phase: 'play', clock: clk(periodMs(rules, 1)), brk: null,
    shotOn: false, shot: clk(rules.shot * 1000), arrow: null, to: null, win: null, finished: false, oppNums: [],
    colors: { us: null, them: null }, names: { us: null, them: null }, common: null };
}
const hex = (v: unknown) => (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? v : null);
const label = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, 30) : null);

/** Un état relu (téléphone, base) : complété, jamais cru sur parole. */
export function readState(x: unknown, now: number): TableState | null {
  if (!x || typeof x !== 'object') return null;
  const s = x as Partial<TableState>;
  if (s.v !== 1 || !s.rules || typeof s.rules !== 'object') return null;
  const base = newState(typeof s.preset === 'string' ? s.preset : 'fiba5', now, { rules: { ...presetOf(String(s.preset)).rules, ...s.rules } });
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const c = (v: unknown, d: Clock): Clock => {
    const x = v as Partial<Clock> | null;
    if (!x || typeof x !== 'object' || num(x.dur) === null) return d;
    return { dur: Math.max(0, x.dur!), start: num(x.start), paused: Math.max(0, num(x.paused) ?? 0), pausedAt: num(x.pausedAt), fix: num(x.fix) ?? 0 };
  };
  return {
    ...base, view: s.view === 'full' ? 'full' : 'simple',
    period: Number.isInteger(s.period) ? Math.min(8, Math.max(1, s.period!)) : 1,
    phase: s.phase === 'break' || s.phase === 'end' ? s.phase : 'play',
    clock: c(s.clock, base.clock), brk: s.brk ? c(s.brk, base.clock) : null, shotOn: !!s.shotOn, shot: c(s.shot, base.shot),
    arrow: s.arrow === 'us' || s.arrow === 'them' ? s.arrow : null,
    to: s.to && (s.to.side === 'us' || s.to.side === 'them') && Number.isFinite(s.to.until) ? s.to : null,
    win: s.win === 'us' || s.win === 'them' ? s.win : null, finished: !!s.finished,
    oppNums: Array.isArray(s.oppNums) ? s.oppNums.filter(n => Number.isInteger(n) && n >= 0 && n <= 99).slice(0, 20) : [],
    colors: { us: hex(s.colors?.us), them: hex(s.colors?.them) }, names: { us: label(s.names?.us), them: label(s.names?.them) },
    common: s.common && typeof s.common.by === 'string' ? { by: s.common.by.slice(0, 40) } : null,
  };
}

/** L'horloge commune arrive (ou s'en va) : la table la suit, en temps courant, sans 24 s ni arrêt de jeu. */
export function withCommon(s: TableState, c: Common | null): TableState {
  if (!c) return s.common ? { ...s, common: null } : s;
  return { ...s, common: { by: c.by }, clock: c.clock, period: c.period ? Math.min(8, Math.max(1, c.period)) : s.period, phase: 'play', brk: null, shotOn: false };
}

// --- Les chronos (heure de la base) -----------------------------------------------------------------------------------
export const isRunning = (c: Clock) => c.start !== null && c.pausedAt === null;
/** Le temps écoulé, le temps restant à l'heure `now`. */
export const elapsedOf = (c: Clock, now: number) => (c.start === null ? 0 : Math.max(0, (c.pausedAt ?? now) - c.start - c.paused));
export const leftOf = (c: Clock, now: number) => Math.max(0, c.dur + c.fix - elapsedOf(c, now));
export const startClock = (c: Clock, now: number): Clock =>
  (c.start === null ? { ...c, start: now, pausedAt: null } : c.pausedAt !== null ? { ...c, paused: c.paused + Math.max(0, now - c.pausedAt), pausedAt: null } : c);
export const stopClock = (c: Clock, now: number): Clock => (isRunning(c) ? { ...c, pausedAt: now } : c);
/** Arrêté à l'instant exact du zéro (le temps restant reste 0, quel que soit le moment où l'on s'en aperçoit). */
const stopAtZero = (c: Clock): Clock => (c.start === null ? c : { ...c, pausedAt: c.start + c.paused + c.dur + c.fix });
/** Une correction : le temps restant devient `ms` (la différence s'ajoute aux corrections). */
export const setLeft = (c: Clock, ms: number, now: number): Clock => ({ ...c, fix: c.fix + (Math.max(0, ms) - leftOf(c, now)) });

/** « 6:42 » ; dixièmes sous 10 s (« 8.4 ») comme le chrono du terrain. */
export function fmtClock(ms: number): string {
  if (ms < 10_000) return `${Math.floor(ms / 1000)}.${Math.floor((ms % 1000) / 100)}`;
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
export const fmtShot = (ms: number) => String(Math.ceil(ms / 1000));

/** QT 2, Prol. 1 ; en 3x3 : Temps de jeu, Prolongation. */
export function periodLabel(r: Rules, p: number, long = false): string {
  if (r.format === '3x3') return p > 1 ? 'Prolongation' : long ? 'Temps de jeu' : 'Match';
  if (p > r.periods) return `Prol.${r.periods + 1 < p ? ` ${p - r.periods}` : ''}`;
  return r.periods === 4 ? `QT ${p}` : `Période ${p}`;
}
/** La colonne « quarter » du journal (1 à 8). */
export const quarterOf = (p: number) => Math.min(8, Math.max(1, p));

/** La pause après la période p : mi-temps au milieu d'un nombre pair de périodes, sinon la pause courte. */
export function breakAfter(r: Rules, p: number): number {
  if (r.format === '5x5' && r.periods % 2 === 0 && p === r.periods / 2) return r.halfMin * 60_000;
  return r.breakMin * 60_000;
}

/** Une seconde qui passe : fin de période (pause, prolongation ou fin du temps réglementaire), fin de pause, de
 *  temps-mort, des 24 s. `ring` : le buzzer doit sonner. */
export function tick(s: TableState, actions: Pick<Action, 'kind' | 'quarter'>[], now: number): { s: TableState; ring: boolean } {
  let ring = false;
  let n = s;
  if (n.to && n.to.until <= now) { n = { ...n, to: null }; ring = true; }
  if (n.shotOn && isRunning(n.shot) && leftOf(n.shot, now) <= 0) {
    n = { ...n, shot: stopAtZero(n.shot), clock: stopClock(n.clock, now) }; ring = true;
  }
  if (n.phase === 'play' && n.rules.clock && isRunning(n.clock) && leftOf(n.clock, now) <= 0) {
    ring = true;
    n = { ...n, clock: stopAtZero(n.clock), shot: stopClock(n.shot, now) };
    // Une horloge commune : c'est l'organisatrice qui enchaîne ; la table attend la suite. Sinon la pause part du zéro exact.
    if (!n.common) n = endOfPeriod(n, actions, Math.min(now, n.clock.pausedAt ?? now));
  }
  if (n.phase === 'break' && n.brk && leftOf(n.brk, now) <= 0) { ring = true; n = nextPeriod(n, now); }
  return { s: n, ring };
}

/** Fin d'une période (chrono à zéro, ou à la main) : la pause, la prolongation, ou la fin du temps réglementaire. */
export function endOfPeriod(s: TableState, actions: Pick<Action, 'kind' | 'quarter'>[], now: number): TableState {
  const r = s.rules, sc = score(actions as Pick<Action, 'kind'>[]);
  const last = s.period >= r.periods;
  if (!last) return { ...s, phase: 'break', brk: clk(breakAfter(r, s.period), now) };
  if (sc.us === sc.them && s.period < 8) return { ...s, phase: 'break', brk: clk(r.breakMin * 60_000, now) };
  // 3x3 : le temps est fini, l'équipe en tête gagne (la même question qu'à 21 points) ; 5x5 : rien d'automatique.
  return { ...s, phase: 'end', win: r.format === '3x3' && !s.finished ? (sc.us > sc.them ? 'us' : 'them') : s.win };
}

/** La période suivante commence : chrono plein et arrêté ; la flèche de possession donne le ballon puis se retourne. */
export function nextPeriod(s: TableState, now: number): TableState {
  const p = Math.min(8, s.period + 1);
  void now;
  return { ...s, period: p, phase: 'play', brk: null, clock: clk(periodMs(s.rules, p)), shot: clk(s.rules.shot * 1000),
    arrow: s.rules.format === '5x5' && s.arrow ? flip(s.arrow) : s.arrow, to: null };
}
export const flip = (x: Side): Side => (x === 'us' ? 'them' : 'us');

/** Le QT changé à la main (table simple, réglages) : chrono plein et arrêté. */
export function setPeriod(s: TableState, p: number, now: number): TableState {
  const q = Math.min(8, Math.max(1, p));
  void now;
  return { ...s, period: q, phase: 'play', brk: null, clock: clk(periodMs(s.rules, q)), shot: clk(s.rules.shot * 1000), to: null };
}

/** Le chrono de jeu : un toucher le lance ou l'arrête (« Ballon mort ») ; les 24 s suivent. */
export function toggleClock(s: TableState, now: number): TableState {
  if (s.common) return s;                                          // horloge commune : pas d'arrêt de jeu
  if (s.phase === 'break') return nextPeriod(s, now);            // pause écourtée : la période suivante, chrono prêt
  if (isRunning(s.clock)) return { ...s, clock: stopClock(s.clock, now), shot: stopClock(s.shot, now) };
  if (s.phase === 'end' || (s.rules.clock && leftOf(s.clock, now) <= 0 && periodMs(s.rules, s.period) > 0)) return s;
  return { ...s, clock: startClock(s.clock, now), shot: s.shotOn && leftOf(s.shot, now) > 0 ? startClock(s.shot, now) : s.shot, to: null };
}
/** Les 24 s (ou 14, ou 12) : remises et reparties si le jeu tourne. */
export function resetShot(s: TableState, secs: number, now: number): TableState {
  return { ...s, shot: clk(secs * 1000, isRunning(s.clock) ? now : null) };
}
/** Un temps-mort : le chrono s'arrête, son décompte commence. */
export function takeTimeout(s: TableState, side: Side, now: number): TableState {
  return { ...s, clock: stopClock(s.clock, now), shot: stopClock(s.shot, now), to: { side, until: now + s.rules.timeoutSec * 1000 } };
}

// --- Le journal : fautes, temps-morts, 21 points ------------------------------------------------------------------
const US_FOULS: ActionKind[] = ['foul', 'foul_t', 'foul_di', 'foul_fl', 'us_foul'];
const THEM_FOULS: ActionKind[] = ['opp_foul', 'opp_foul_t', 'opp_foul_di', 'opp_foul_fl'];
export const isTeamFoul = (k: ActionKind, side: Side) => (side === 'us' ? US_FOULS : THEM_FOULS).includes(k);

/** Fautes d'équipe de la période (5x5 : les prolongations comptent avec le dernier QT ; 3x3 : tout le match). */
export function teamFouls(actions: Pick<Action, 'kind' | 'quarter'>[], r: Rules, period: number, side: Side): number {
  const bucket = (q: number) => (r.format === '3x3' ? 0 : Math.min(q, r.periods));
  const b = bucket(period);
  return actions.filter(a => isTeamFoul(a.kind, side) && bucket(a.quarter) === b).length;
}
/** « Pénalité » (5x5, après 4) ; 3x3 : « bonus » de la 7e à la 9e (2 LF), « bonus+ » dès la 10e (2 LF et le ballon). */
export function foulState(r: Rules, n: number): 'none' | 'penalty' | 'bonus' | 'bonus+' {
  if (r.format === '3x3') return n >= 10 ? 'bonus+' : n > r.teamFouls ? 'bonus' : 'none';
  return n >= r.teamFouls ? 'penalty' : 'none';
}
export const foulBadge = (r: Rules, n: number): string | null =>
  ({ none: null, penalty: 'Pénalité', bonus: 'Bonus · 2 LF', 'bonus+': '2 LF + ballon' } as const)[foulState(r, n)];

export interface PlayerFouls { p: number; t: number; di: number; fl: number; total: number; out: boolean; gd: boolean; letters: string }
const LETTER: Partial<Record<ActionKind, string>> = { foul: 'P', foul_t: 'T', foul_di: 'DI', foul_fl: 'FL', opp_foul: 'P', opp_foul_t: 'T', opp_foul_di: 'DI', opp_foul_fl: 'FL' };
/** Les fautes d'une joueuse (person_id) ou d'un numéro adverse (« #11 ») : 5 → elle sort ; 2 T, 2 FL ou T + FL → exclue. */
export function playerFouls(actions: Pick<Action, 'kind' | 'person_id' | 'opp_number'>[], who: string, r: Rules): PlayerFouls {
  const mine = actions.filter(a => (who.startsWith('#') ? a.kind.startsWith('opp') && a.opp_number === Number(who.slice(1)) : a.person_id === who) && LETTER[a.kind]);
  const c = (s: string) => mine.filter(a => LETTER[a.kind] === s).length;
  const f = { p: c('P'), t: c('T'), di: c('DI'), fl: c('FL') };
  const total = mine.length;
  return { ...f, total, out: r.playerFouls !== null && total >= r.playerFouls, gd: f.t + f.fl >= 2, letters: mine.map(a => LETTER[a.kind]).join(' ') };
}

/** Temps-morts restants pour la période en cours (art. 18 ; 3x3 : un par match). */
export function timeoutsLeft(actions: Pick<Action, 'kind' | 'quarter' | 'game_ms'>[], r: Rules, period: number, side: Side, clockLeft: number): number {
  const tos = actions.filter(a => a.kind === (side === 'us' ? 'us_to' : 'opp_to'));
  if (r.format === '3x3') return Math.max(0, r.timeouts[0] - tos.length);
  if (period > r.periods) return Math.max(0, r.timeouts[2] - tos.filter(a => a.quarter === period).length);
  const half = Math.ceil(r.periods / 2);
  const first = period <= half;
  const used = tos.filter(a => (first ? a.quarter <= half : a.quarter > half && a.quarter <= r.periods)).length;
  let left = Math.max(0, (first ? r.timeouts[0] : r.timeouts[1]) - used);
  if (!first && r.lateMax !== null && period === r.periods && clockLeft <= 120_000) {
    const late = tos.filter(a => a.quarter === r.periods && a.game_ms !== null && a.game_ms !== undefined && a.game_ms <= 120_000).length;
    left = Math.min(left, Math.max(0, r.lateMax - late));
  }
  return left;
}
/** Le maximum de la période (pour dessiner les pastilles). */
export function timeoutsMax(r: Rules, period: number): number {
  if (r.format === '3x3') return r.timeouts[0];
  if (period > r.periods) return r.timeouts[2];
  return period <= Math.ceil(r.periods / 2) ? r.timeouts[0] : r.timeouts[1];
}

/** Corriger le chrono (arrêté) : le temps restant de la période, la période, les 24 s / 12 s. Rend l'action du journal
 *  qui garde la trace (« Chrono corrigé : 6:42 → 7:00 · QT 2 »), ou null si rien ne change. */
export function fixClock(s: TableState, o: { left?: number; period?: number; shot?: number }, now: number):
  { s: TableState; fixes: { kind: 'clock_fix' | 'shot_fix'; game_ms: number; meta: Record<string, number> }[] } {
  let n = s;
  const fixes: { kind: 'clock_fix' | 'shot_fix'; game_ms: number; meta: Record<string, number> }[] = [];
  if (s.common) return { s, fixes };
  if (o.period !== undefined && o.period !== s.period) {
    const from = s.period;
    n = setPeriod(n, o.period, now);
    fixes.push({ kind: 'clock_fix', game_ms: leftOf(n.clock, now), meta: { q_from: from, q_to: n.period } });
  }
  if (o.left !== undefined) {
    const before = Math.round(leftOf(n.clock, now)), after = Math.max(0, Math.round(o.left));
    if (before !== after) { n = { ...n, clock: setLeft(n.clock, after, now), phase: n.phase === 'end' && after > 0 ? 'play' : n.phase }; fixes.push({ kind: 'clock_fix', game_ms: after, meta: { from: before, to: after } }); }
  }
  if (o.shot !== undefined && n.shotOn) {
    const before = Math.round(leftOf(n.shot, now)), after = Math.max(0, Math.round(o.shot));
    if (before !== after) { n = { ...n, shot: setLeft(n.shot, after, now) }; fixes.push({ kind: 'shot_fix', game_ms: after, meta: { from: before, to: after } }); }
  }
  return { s: n, fixes };
}

/** 3x3 : 21 points dans le temps, ou 2 points marqués en prolongation : qui gagne ? (null : personne encore). */
export function winner(actions: Pick<Action, 'kind' | 'quarter'>[], r: Rules, period: number): Side | null {
  if (r.format !== '3x3' || !r.target) return null;
  if (period <= r.periods) {
    const sc = score(actions as Pick<Action, 'kind'>[]);
    if (sc.us >= r.target || sc.them >= r.target) return sc.us >= sc.them ? 'us' : 'them';
    return null;
  }
  const ot = score(actions.filter(a => a.quarter > r.periods) as Pick<Action, 'kind'>[]);
  const need = r.otTarget ?? 2;
  return ot.us >= need ? 'us' : ot.them >= need ? 'them' : null;
}

/** Après une action : la question « Victoire de … ? » s'ouvre (3x3) si un score gagnant est atteint. */
export function afterAction(s: TableState, actions: Pick<Action, 'kind' | 'quarter'>[], kind: ActionKind, now: number): TableState {
  if (s.rules.format !== '3x3' || s.finished || !pointsOf(kind)) return s;
  const w = winner(actions, s.rules, s.period);
  return w ? { ...s, win: w, clock: stopClock(s.clock, now), shot: stopClock(s.shot, now) } : { ...s, win: null };
}

// --- Les phrases du journal ------------------------------------------------------------------------------------------
const WHAT: Partial<Record<ActionKind, string>> = {
  ft: 'LF', p2: '+2', p3: '+3', us1: '+1', us2: '+2', us3: '+3', opp1: '+1', opp2: '+2', opp3: '+3', us_ft: 'LF', opp_ft: 'LF',
  us_fix: '−1', opp_fix: '−1', us_to: 'temps-mort', opp_to: 'temps-mort', clock_fix: 'chrono corrigé', shot_fix: 'chrono des tirs corrigé',
  foul: 'faute', opp_foul: 'faute', foul_t: 'technique', opp_foul_t: 'technique', foul_di: 'disruptive', opp_foul_di: 'disruptive',
  foul_fl: 'flagrante', opp_foul_fl: 'flagrante', us_foul: 'faute d’équipe',
};
export interface Names { us: string; them: string; person: (id: string) => { num: number | null; name: string | null } }
/** « N° 9 Nora · faute (4e) », « Valbrenne n° 11 · +2 », « Rivebelle 1 · +2 · 21–17 ». */
export function sentence(a: Action, all: Action[], n: Names, r: Rules): { who: string; what: string } {
  const what = WHAT[a.kind] ?? a.kind;
  if (a.kind === 'clock_fix' || a.kind === 'shot_fix') return { who: 'Table', what: fixText(a, r) };
  let who: string;
  if (a.person_id) { const p = n.person(a.person_id); who = [p.num !== null ? `N° ${p.num}` : null, p.name].filter(Boolean).join(' ') || n.us; }
  else if (a.kind.startsWith('opp')) who = a.opp_number !== null && a.opp_number !== undefined ? `${n.them} n° ${a.opp_number}` : n.them;
  else who = n.us;
  const upto = all.slice(0, all.indexOf(a) + 1);
  if (LETTER[a.kind]) {
    const key = a.person_id ?? (a.opp_number !== null && a.opp_number !== undefined ? `#${a.opp_number}` : null);
    if (key) { const f = playerFouls(upto, key, r); return { who, what: `${what} (${f.total}e)` }; }
    const side: Side = a.kind.startsWith('opp') ? 'them' : 'us';
    const tf = teamFouls(upto, r, a.quarter, side);
    return { who, what: `${tf}e faute${foulBadge(r, tf) && r.format === '3x3' ? ' · bonus' : ''}` };
  }
  if (a.kind === 'us_foul') { const tf = teamFouls(upto, r, a.quarter, 'us'); return { who, what: `${tf}e faute${r.format === '3x3' && tf > r.teamFouls ? ' · bonus' : ''}` }; }
  if (pointsOf(a.kind) && r.format === '3x3') { const sc = score(upto); return { who, what: `${what} · ${sc.us}–${sc.them}` }; }
  return { who, what };
}

/** « Chrono corrigé : 6:42 → 7:00 · QT 2 », « Période corrigée : QT 2 → QT 3 », « 24 s corrigées : 14 → 24 ». */
export function fixText(a: Pick<Action, 'kind' | 'quarter' | 'meta'>, r: Rules): string {
  const m = a.meta ?? {};
  if (a.kind === 'shot_fix') return `${r.shot} s corrigées : ${Math.ceil((m.from ?? 0) / 1000)} → ${Math.ceil((m.to ?? 0) / 1000)}`;
  if (m.q_from !== undefined && m.q_to !== undefined) return `Période corrigée : ${periodLabel(r, m.q_from)} → ${periodLabel(r, m.q_to)}`;
  return `Chrono corrigé : ${fmtClock(m.from ?? 0)} → ${fmtClock(m.to ?? 0)} · ${periodLabel(r, a.quarter)}`;
}
export const isFix = (k: ActionKind) => k === 'clock_fix' || k === 'shot_fix';

/** La marque courante : chaque panier, le total après lui et le numéro de la marqueuse (feuille du club). */
export function running(actions: Action[], numberOf: (personId: string) => number | null): Record<Side, { total: number; num: number | null; q: number }[]> {
  const out: Record<Side, { total: number; num: number | null; q: number }[]> = { us: [], them: [] };
  const tot = { us: 0, them: 0 };
  for (const a of actions) {
    const p = pointsOf(a.kind);
    if (!p) continue;
    tot[p.side] += p.pts;
    if (p.pts <= 0) { out[p.side] = out[p.side].filter(x => x.total <= tot[p.side]); continue; }   // le « −1 » efface la case
    out[p.side].push({ total: tot[p.side], num: a.person_id ? numberOf(a.person_id) : a.opp_number ?? null, q: a.quarter });
  }
  return out;
}

// --- La file d'envoi (hors réseau) ----------------------------------------------------------------------------------
export type Op = { op: 'add'; a: Action } | { op: 'del'; id: string } | { op: 'finish' };
/** Annuler une action pas encore partie : elle disparaît de la file (rien n'est envoyé) ; sinon, l'effacement part. */
export function queueDel(q: Op[], id: string): Op[] {
  return q.some(o => o.op === 'add' && o.a.id === id) ? q.filter(o => !(o.op === 'add' && o.a.id === id)) : [...q, { op: 'del', id }];
}
/** Le journal vu par la table : celui de la base, plus ce qui attend dans la file, moins ce qui y est effacé. */
export function mergeJournal(server: Action[], local: Action[], q: Op[]): Action[] {
  const del = new Set(q.flatMap(o => (o.op === 'del' ? [o.id] : [])));
  const seen = new Set<string>();
  const out: Action[] = [];
  for (const a of [...local, ...server]) {
    if (seen.has(a.id) || del.has(a.id)) continue;
    seen.add(a.id); out.push(a);
  }
  return out.sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/** Un identifiant d'action tiré sur le téléphone (aucun doublon à l'envoi). */
export function newId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b); else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40; b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
